import './ModelDiagnosticsModal.scss';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { ChartContainer } from '@Components/ChartContainer';
import { ChartTooltipV2, KeyShapeOptions } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Brand0, Brand30, Brand50, Neutral400, Neutral600 } from '@src/utils/colors';
import { downloadPNG, exportMultiSheetToExcel } from '@src/utils/download-utils';
import { ActualVsPredictedRow, DashboardData } from '../dtc-commerce-mmm-types';
import { DownloadActions } from './ChannelDrawer/channel-drawer-types';
import { shapeActualVsPredictedExport } from '../utils/dtc-commerce-mmm-export';
import { formatCurrency, formatWholeNumber } from '../utils/dtc-commerce-mmm-format';
import {
  mapeQuality,
  Quality,
  QUALITY_LABELS,
  r2Quality
} from '../utils/dtc-commerce-mmm-model-fit';
import { findYearBoundaries, TimeAggConst } from '../utils/dtc-commerce-mmm-year-markers';
import {
  useElementWidth,
  YEAR_MARKER_STROKE,
  YearMarkerLabel
} from '../utils/dtc-commerce-mmm-year-marker-ui';
import { useDtcCommerceMmmData } from '../context/DtcCommerceMmmDataContext';

interface ModelDiagnosticsModalProps {
  show: boolean;
  onHide: () => void;
  data: DashboardData;
}

interface TooltipPayloadItem {
  payload?: ActualVsPredictedRow;
}

interface AvpTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  isRevenue: boolean;
  kpiLabel: string;
  actualColor: string;
  predictedColor: string;
}

const AvpTooltip = ({
  active,
  payload,
  isRevenue,
  kpiLabel,
  actualColor,
  predictedColor
}: AvpTooltipProps) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const fmt = isRevenue ? formatCurrency : formatWholeNumber;
  const dateLabel = new Date(`${row.date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const phaseLabel = row.trainTest === 'test' ? 'Test window' : 'Train window';
  return (
    <ChartTooltipV2
      headers={[dateLabel, phaseLabel]}
      items={[
        {
          label: `Actual ${kpiLabel}`,
          value: fmt(row.actualKpi),
          color: actualColor,
          keyShape: KeyShapeOptions.LINE
        },
        {
          label: `Predicted ${kpiLabel}`,
          value: fmt(row.expectedKpi),
          color: predictedColor,
          keyShape: KeyShapeOptions.DASHED_LINE
        }
      ]}
    />
  );
};

interface ActualVsPredictedChartProps {
  data: ActualVsPredictedRow[];
  isRevenue: boolean;
  download: DownloadActions;
}

// Matches the Executive Summary spark charts: solid purple + shading = actual,
// dashed gray = predicted.
const ACTUAL_COLOR = Brand50;
const PREDICTED_COLOR = Neutral600;
const ACTUAL_GRADIENT_ID = 'modelDiagnosticsActualGrad';
const CHART_MARGIN_LEFT = 8;
const CHART_MARGIN_RIGHT = 16;
const CHART_Y_AXIS_WIDTH = 64;

// Cap the number of month labels so long model windows don't crowd the axis;
// the step widens (every 2nd, 3rd… month) as the window grows.
const MAX_MONTH_TICKS = 12;

const formatMonthTick = (date: string): string => {
  const d = new Date(`${date}T00:00:00`);
  return `${d.toLocaleDateString('en-US', { month: 'short' })} '${String(d.getFullYear()).slice(-2)}`;
};

const ActualVsPredictedChart = ({ data, isRevenue, download }: ActualVsPredictedChartProps) => {
  const sorted = useMemo(() => [...data].sort((a, b) => a.date.localeCompare(b.date)), [data]);

  const monthTicks = useMemo(() => {
    const firstDateOfMonth = new Map<string, string>();
    sorted.forEach(r => {
      const monthKey = r.date.slice(0, 7);
      if (!firstDateOfMonth.has(monthKey)) {
        firstDateOfMonth.set(monthKey, r.date);
      }
    });
    const months = [...firstDateOfMonth.values()];
    const step = Math.ceil(months.length / MAX_MONTH_TICKS);
    return months.filter((_, i) => i % step === 0);
  }, [sorted]);

  const yearBoundaries = useMemo(
    () =>
      findYearBoundaries(
        sorted.map(r => r.date),
        TimeAggConst.WEEKLY
      ),
    [sorted]
  );

  const { ref: chartRef, width: chartWidth } = useElementWidth();
  const kpiLabel = isRevenue ? 'Revenue' : 'Conversions';
  const fmtY = isRevenue ? formatCurrency : formatWholeNumber;

  if (sorted.length === 0) return null;

  return (
    <ChartContainer
      className='model-diagnostics-avp'
      title={
        <div className='dtc-commerce-mmm-title-stack'>
          <div>Actual vs Predicted {kpiLabel}</div>
          <div className='dtc-commerce-mmm-title-stack-sub'>
            Weekly fit — solid purple line is what actually happened; dashed gray line is what the
            model predicted
          </div>
        </div>
      }
      viewRightActions
      rightActions={<DownloadDropdown onClickOptions={[download.onExcel, download.onPng]} />}
    >
      <div className='model-diagnostics-chart-stack' ref={chartRef}>
        <ResponsiveContainer width='100%' height={300}>
          <ComposedChart
            data={sorted}
            margin={{
              top: 24,
              right: CHART_MARGIN_RIGHT,
              bottom: 0,
              left: CHART_MARGIN_LEFT
            }}
          >
            <defs>
              <linearGradient id={ACTUAL_GRADIENT_ID} x1='0' y1='0' x2='0' y2='1'>
                <stop offset='1%' stopColor={Brand30} />
                <stop offset='95%' stopColor={Brand0} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey='date'
              ticks={monthTicks}
              tickFormatter={formatMonthTick}
              tick={{ fontSize: 10, fill: Neutral600 }}
              tickLine={false}
              axisLine={{ stroke: Neutral400 }}
            />
            <YAxis
              tickFormatter={v => fmtY(v)}
              tick={{ fontSize: 10, fill: Neutral600 }}
              width={CHART_Y_AXIS_WIDTH}
              axisLine={{ stroke: Neutral400 }}
              tickLine={false}
            />
            {yearBoundaries.map(({ dateKey, year }) => (
              <ReferenceLine
                key={`yr-${dateKey}`}
                x={dateKey}
                stroke={YEAR_MARKER_STROKE}
                strokeDasharray='4 4'
                strokeWidth={1}
                label={
                  <YearMarkerLabel
                    year={year}
                    chartWidth={chartWidth}
                    marginLeft={CHART_MARGIN_LEFT}
                    marginRight={CHART_MARGIN_RIGHT}
                    yAxisWidth={CHART_Y_AXIS_WIDTH}
                  />
                }
              />
            ))}
            <Tooltip
              content={
                <AvpTooltip
                  isRevenue={isRevenue}
                  kpiLabel={kpiLabel}
                  actualColor={ACTUAL_COLOR}
                  predictedColor={PREDICTED_COLOR}
                />
              }
            />
            <Area
              type='linear'
              dataKey='actualKpi'
              stroke={ACTUAL_COLOR}
              strokeWidth={3}
              fill={`url(#${ACTUAL_GRADIENT_ID})`}
              fillOpacity={1}
              isAnimationActive={false}
              activeDot={{ r: 4, fill: ACTUAL_COLOR, strokeWidth: 0 }}
            />
            <Line
              type='linear'
              dataKey='expectedKpi'
              stroke={PREDICTED_COLOR}
              strokeWidth={2}
              strokeDasharray='4'
              dot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <div className='model-diagnostics-legend'>
          <div className='model-diagnostics-legend-item'>
            <span className='model-diagnostics-legend-swatch solid' />
            Actual {kpiLabel}
          </div>
          <div className='model-diagnostics-legend-item'>
            <span className='model-diagnostics-legend-swatch dashed' />
            Predicted {kpiLabel}
          </div>
        </div>
      </div>
    </ChartContainer>
  );
};

interface MetricTileProps {
  label: string;
  value: string;
  context: string;
  quality?: Quality;
}

const MetricTile = ({ label, value, context, quality }: MetricTileProps) => (
  <div className='model-diagnostics-metric'>
    <div className={cn('model-diagnostics-tile', quality)}>
      <div className='model-diagnostics-tile-label'>{label}</div>
      <div className='model-diagnostics-tile-value'>{value}</div>
      <div className='model-diagnostics-tile-context'>{context}</div>
    </div>
    {quality && (
      <div className={cn('model-diagnostics-quality', quality)}>{QUALITY_LABELS[quality]}</div>
    )}
  </div>
);

const MetricHealthBody = () => (
  <div className='metric-health'>
    <table className='metric-health-table'>
      <thead>
        <tr>
          <th>Metric</th>
          <th>
            <span className='metric-health-dot strong' /> Strong
          </th>
          <th>
            <span className='metric-health-dot moderate' /> Moderate
          </th>
          <th>
            <span className='metric-health-dot weak' /> Weak
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>R² (Train &amp; Test)</td>
          <td>&gt; 0.85</td>
          <td>0.70 – 0.85</td>
          <td>&lt; 0.70</td>
        </tr>
        <tr>
          <td>MAPE</td>
          <td>&lt; 10%</td>
          <td>10% – 20%</td>
          <td>&gt; 20%</td>
        </tr>
      </tbody>
    </table>
    <p className='metric-health-description'>
      R² measures how well the model explains variance in the data (1.0 = perfect). MAPE measures
      the average percentage error of predictions (lower is better).
    </p>
  </div>
);

interface MetricHealthDialogProps {
  show: boolean;
  onHide: () => void;
}

const MetricHealthDialog = ({ show, onHide }: MetricHealthDialogProps) => (
  <AlertDialog
    show={show}
    onHide={onHide}
    title='Metric Health'
    body={<MetricHealthBody />}
    primaryAction={{ label: 'Close' }}
    className='metric-health-dialog'
    passthroughProps={{ backdropClassName: 'metric-health-backdrop' }}
  />
);

const ModelDiagnosticsBody = ({ data }: { data: DashboardData }) => {
  const { modelInfo, actualVsPredicted } = data;
  const isRevenue = modelInfo.kpiType === 'revenue';
  const { buildExportFilename } = useDtcCommerceMmmData();
  const [showLegend, setShowLegend] = useState(false);

  // Strip the download dropdown itself from the PNG snapshot so it doesn't
  // overlay the chart in the exported image.
  const avpFilename = buildExportFilename(modelInfo.client, 'actual-vs-predicted');
  const avpDownload: DownloadActions = {
    onExcel: () => exportMultiSheetToExcel(shapeActualVsPredictedExport(data), avpFilename),
    onPng: () => downloadPNG('.model-diagnostics-avp', avpFilename, '', ['.download'])
  };

  return (
    <div className='model-diagnostics'>
      <Button
        size={Size.SM}
        className='model-diagnostics-health-trigger'
        onClick={() => setShowLegend(true)}
      >
        Metric Health
      </Button>
      <MetricHealthDialog show={showLegend} onHide={() => setShowLegend(false)} />
      <div className='model-diagnostics-tiles'>
        <MetricTile
          label='Train R²'
          value={modelInfo.trainRsquared.toFixed(3)}
          context='In-sample fit'
          quality={r2Quality(modelInfo.trainRsquared)}
        />
        <MetricTile
          label='Test R²'
          value={modelInfo.testRsquared.toFixed(3)}
          context='Out-of-sample fit'
          quality={r2Quality(modelInfo.testRsquared)}
        />
        <MetricTile
          label='Test MAPE'
          value={`${modelInfo.testMape.toFixed(1)}%`}
          context='Mean abs % error'
          quality={mapeQuality(modelInfo.testMape)}
        />
        <MetricTile
          label='Observations'
          value={modelInfo.nObservations.toString()}
          context={`${Math.round(modelInfo.trainSize * 100)}% train + ${Math.round(modelInfo.testSize * 100)}% test`}
        />
      </div>

      <ActualVsPredictedChart
        data={actualVsPredicted}
        isRevenue={isRevenue}
        download={avpDownload}
      />
    </div>
  );
};

export const ModelDiagnosticsModal = ({ show, onHide, data }: ModelDiagnosticsModalProps) => (
  <AlertDialog
    show={show}
    onHide={onHide}
    title='Model Diagnostics'
    body={<ModelDiagnosticsBody data={data} />}
    primaryAction={{ label: 'Close' }}
    className='model-diagnostics-dialog'
  />
);
